import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Crown } from "lucide-react";
import { LOGO_URL } from "../lib/api";
import { usePro } from "../hooks/usePro";
import { useAuth } from "../hooks/useAuth";

const LINKS = [
  { label: "Home", to: "/home" },
  { label: "Categories", to: "/categories" },
  { label: "AI Resources", to: "/ai-resources" },
  { label: "Learn", to: "/learn" },
  { label: "Business Ideas", to: "/business-ideas" },
  { label: "Web & App Studios", to: "/apps" },
  { label: "Startup Notebook", to: "/notebook" },
  { label: "Profile", to: "/profile" },
];

export const Navbar = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const { isPro, openUpgrade } = usePro();
  const { user } = useAuth();

  const isActive = (to) => (to === "/" ? pathname === "/" : pathname.startsWith(to));

  return (
    <nav
      className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-100"
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="nav-logo">
            <img src={LOGO_URL} alt="LaunchBS" className="h-11 w-11 object-contain transition-transform duration-300 group-hover:scale-110" />
            <span className="font-display text-2xl font-extrabold tracking-tight text-[#0F0F0F]">
              LAUNCH<span className="text-[#D4AF37]">BS</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-9">
            {LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/ /g, "-")}-link`}
                className={`relative text-sm font-medium transition-colors duration-200 ${
                  isActive(l.to) ? "text-[#0F0F0F]" : "text-[#687280] hover:text-[#0F0F0F]"
                }`}
              >
                {l.label}
                {isActive(l.to) && (
                  <span className="absolute -bottom-2 left-0 right-0 h-0.5 bg-[#D4AF37] rounded-full" />
                )}
              </Link>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-4">
            {isPro ? (
              <span
                className="inline-flex items-center gap-1.5 text-xs font-semibold bg-[#D4AF37]/15 text-[#B8901F] px-3 py-1.5 rounded-full"
                data-testid="nav-pro-badge"
              >
                <Crown size={13} /> PRO
              </span>
            ) : (
              <button
                onClick={() => openUpgrade("default")}
                className="inline-flex items-center gap-1.5 text-sm font-semibold bg-[#0F0F0F] text-[#D4AF37] px-4 py-2 rounded-full hover:bg-[#1c1c1c] transition-colors"
                data-testid="nav-go-pro-btn"
              >
                <Crown size={15} /> Go Pro
              </button>
            )}
            <Link
              to="/profile"
              data-testid="nav-avatar"
              className="w-10 h-10 rounded-full bg-[#0F0F0F] text-[#D4AF37] flex items-center justify-center font-semibold overflow-hidden hover:ring-2 hover:ring-[#D4AF37]/40 transition-all"
            >
              {user?.picture ? (
                <img src={user.picture} alt={user.name || "Profile"} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
              ) : (
                "L"
              )}
            </Link>
          </div>

          <button
            className="md:hidden text-[#0F0F0F]"
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-toggle"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-gray-100 bg-white px-6 py-4 flex flex-col gap-4">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              data-testid={`nav-mobile-${l.label.toLowerCase().replace(/ /g, "-")}-link`}
              className={`text-base font-medium ${isActive(l.to) ? "text-[#D4AF37]" : "text-[#0F0F0F]"}`}
            >
              {l.label}
            </Link>
          ))}
        </div>
      )}
    </nav>
  );
};
